/**
 * Eine Zeitraum-Zeile als gestapelte Karte — die Komposition unterhalb der Tabellenbreite.
 *
 * Zehn Spalten passen in kein Telefon; im Dialog lag die Tabelle dort hinter einem horizontalen
 * Scroll, und ausgerechnet die Beschränkungen und die Entfernen-Schaltfläche standen ganz rechts.
 */
import { X } from 'lucide-react';
import { FormField } from '@/shared/components';
import {
  RangeAvailabilitySelect,
  RangeFromInput,
  RangeMaxStayInput,
  RangeMinStayInput,
  RangeRateInput,
  RangeToInput,
} from './multiRangeControls';
import { RangeRestrictionCheck, RESTRICTIONS } from './multiRangeRestrictions';
import type { MultiRangeRow as Row } from './multiRangeRowModel';

interface Props {
  row: Row;
  index: number;
  onChange: (patch: Partial<Row>) => void;
  onRemove: () => void;
  disabled: boolean;
}

export function MultiRangeCard({ row, index, onChange, onRemove, disabled }: Props) {
  const control = { row, index, onChange, disabled };
  return (
    <li className="border-line bg-card-alt/30 rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-ink font-serif text-base">Zeitraum {index + 1}</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="text-ink-soft hover:text-sealwax -my-2 -mr-2 inline-flex size-11 items-center justify-center rounded disabled:opacity-40"
          aria-label={`Zeitraum ${index + 1} entfernen`}
        >
          <X size={16} aria-hidden />
        </button>
      </div>

      {/*
        Auf 360 px bleiben rund 296 px für die Karte. Zwei Spalten schnitten dort dem
        Datumsfeld die Jahreszahl ab („19.08.202") und dem Preisfeld seinen Platzhalter.
        Erst ab genug Breite stehen die Felder paarweise.
      */}
      <div className="@sm:grid-cols-2 grid grid-cols-1 gap-3">
        <FormField label="Beginn">
          <RangeFromInput {...control} />
        </FormField>
        <FormField label="Ende">
          <RangeToInput {...control} />
        </FormField>
        <FormField label="Preis je Nacht">
          <RangeRateInput {...control} />
        </FormField>
        <FormField label="Verfügbarkeit">
          <RangeAvailabilitySelect {...control} />
        </FormField>
        <FormField label="Mindestaufenthalt">
          <RangeMinStayInput {...control} />
        </FormField>
        <FormField label="Höchstaufenthalt">
          <RangeMaxStayInput {...control} />
        </FormField>
      </div>

      <fieldset className="mt-3">
        <legend className="text-ink-faint mb-1.5 text-[11px] uppercase tracking-wider">
          Beschränkungen
        </legend>
        <div className="flex flex-col gap-1">
          {RESTRICTIONS.map((field) => (
            <label
              key={field.key}
              className="text-ink-soft flex min-h-11 items-center justify-between gap-2 text-sm"
            >
              {field.label}
              <RangeRestrictionCheck {...control} field={field} />
            </label>
          ))}
        </div>
      </fieldset>
    </li>
  );
}
